import { View, Text, TextInput, Pressable, FlatList, StyleSheet, Platform, ActivityIndicator, Animated, Image, Modal } from 'react-native'
import { KeyboardAvoidingView } from 'react-native-keyboard-controller'
import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context'
import { useRouter, useLocalSearchParams } from 'expo-router'
import { Send, User, Mail, RotateCcw, ArrowRight, Edit3, CheckCircle2, AlertTriangle, AlertCircle, XCircle, PenLine, Inbox, Zap, BookOpen, Link2, X, Save, BarChart3, Rocket, ChevronDown, ChevronUp, Clock, Globe, RefreshCw } from 'lucide-react-native'
import { toast } from '../../src/components/Toast'
import { useFeedbackTrigger } from '../../src/hooks/useFeedbackTrigger'
import { apiClient } from '../../src/api/client'
import { getCachedDashboardSavings } from '../../src/utils/savings'
import { storage, USER_ID_KEY } from '../../src/utils/storage'
import { colors, spacing, borderRadius, typography, shadows } from '../../src/theme'
import { events } from '../../src/lib/posthog'
import PressableScale from '../../src/components/PressableScale'
import ContentContainer from '../../src/components/ContentContainer'
import { appEvents } from '../../src/lib/events'
import { haptics } from '../../src/utils/haptics'
import { sentryCaptureException } from '../../src/lib/sentry'

export { ScreenErrorBoundary as ErrorBoundary } from '../../src/components/ScreenErrorBoundary'

interface EmailSuggestion {
  provider_name: string
  action_type: string
  reason: string
}

interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
  timestamp: Date
  isError?: boolean
  actionCards?: { type: string; label: string; description?: string; href: string; icon?: string }[]
  emailSuggestion?: EmailSuggestion
  emailDraft?: any
  draftStatus?: 'pending' | 'editing' | 'sending' | 'sent' | 'failed'
}

const DEFAULT_SUGGESTIONS = [
  'Hvad kan du hjælpe mig med?',
  'Hvordan sparer jeg på strømmen?',
  'Hvad koster mit mobilabonnement mig?',
  'Hvornår er strømmen billigst?',
]

const CHAT_KEY = 'forbrugeragent_chat'

// Icon mapping for server-provided card.icon field (matches Next.js ACTION_ICONS)
const ACTION_ICONS: Record<string, any> = {
  Mail,
  Inbox,
  BarChart3,
  Rocket,
  X,
  RefreshCw,
}

export default function ChatScreen() {
  const router = useRouter()
  const insets = useSafeAreaInsets()
  const params = useLocalSearchParams<{ inbox_message_id?: string; provider?: string }>()
  const inboxMessageId = params.inbox_message_id
  const providerParam = params.provider

  const sessionKey = inboxMessageId ? `inbox_${inboxMessageId}` : 'general'

  const defaultWelcome: ChatMessage = {
    role: 'assistant',
    content: inboxMessageId && providerParam
      ? `Hej! Jeg kan se du har en besked fra ${providerParam}. Hvad vil du gerne gøre?`
      : 'Hej! Jeg er din personlige forbruger-rådgiver. Hvad kan jeg hjælpe dig med?',
    timestamp: new Date(),
  }

  const [messages, setMessages] = useState<ChatMessage[]>([defaultWelcome])
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [lastFailedMessage, setLastFailedMessage] = useState<string | null>(null)
  const [userContext, setUserContext] = useState<any>(null)
  const [cancelConfirm, setCancelConfirm] = useState<{ type: string; label: string; description?: string; href: string } | null>(null)
  const [showIdleSuggestions, setShowIdleSuggestions] = useState(false)
  const [expandedDrafts, setExpandedDrafts] = useState<Set<number>>(new Set())
  const [slowLoading, setSlowLoading] = useState(false)
  const [isWebSearchQuery, setIsWebSearchQuery] = useState(false)
  const [chatEngaged, setChatEngaged] = useState(false)
  const flatListRef = useRef<FlatList>(null)
  const userMessageCountRef = useRef(0)
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useFeedbackTrigger('chat_engaged', 10_000, 'Fik du svar på det du søgte?', chatEngaged)

  // Auth guard — redirect to login if no user_id (matches Next.js)
  useEffect(() => {
    const checkAuth = async () => {
      const userId = await storage.get(USER_ID_KEY)
      if (!userId) { router.replace('/login' as any) }
    }
    checkAuth()
  }, [router])

  // Load saved session — reset if older than 10 min (UI stays fresh; AI memory lives in DB)
  useEffect(() => {
    const load = async () => {
      try {
        const raw = await storage.get(`${CHAT_KEY}_${sessionKey}`)
        if (raw) {
          const data = JSON.parse(raw)
          const age = Date.now() - (data.savedAt || 0)
          if (age > 10 * 60 * 1000) {
            // Session expired (10 min) — clear stored data, keep default welcome
            await storage.remove(`${CHAT_KEY}_${sessionKey}`)
            return
          }
          if (data.messages?.length > 0) {
            setMessages(data.messages.map((m: any) => ({ ...m, timestamp: new Date(m.timestamp) })))
          }
        }
      } catch {}
    }
    load()
  }, [sessionKey])

  // Save session on message change
  useEffect(() => {
    if (messages.length > 1) {
      const data = {
        messages: messages.slice(-2000).map(m => ({ ...m, timestamp: m.timestamp.toISOString() })),
        savedAt: Date.now(),
      }
      storage.set(`${CHAT_KEY}_${sessionKey}`, JSON.stringify(data)).catch(() => {})
    }
  }, [messages, sessionKey])

  // Load user context
  useEffect(() => {
    apiClient.chat.getContext().then((res: any) => setUserContext(res.data)).catch(() => {})
  }, [])

  // Re-show suggestions after 2 minutes of inactivity
  useEffect(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current)
    setShowIdleSuggestions(false)
    if (messages.length > 2 && !isLoading) {
      idleTimerRef.current = setTimeout(() => setShowIdleSuggestions(true), 120_000)
    }
    return () => { if (idleTimerRef.current) clearTimeout(idleTimerRef.current) }
  }, [messages, isLoading])

  // Show "Det tager lidt længere..." after 15 seconds
  useEffect(() => {
    if (isLoading) {
      setSlowLoading(false)
      const timer = setTimeout(() => setSlowLoading(true), 15000)
      return () => clearTimeout(timer)
    }
    setSlowLoading(false)
  }, [isLoading])

  const conversationHistory = useMemo(() =>
    messages.filter(m => !m.isError).map(m => ({ role: m.role, content: m.content })),
    [messages]
  )

  const suggestions = useMemo(() => {
    if (inboxMessageId && providerParam) {
      return [
        `Hvad handler denne besked fra ${providerParam} om?`,
        `Hjælp mig med at svare ${providerParam}`,
        `Jeg vil klage til ${providerParam}`,
        `Jeg vil opsige hos ${providerParam}`,
      ]
    }
    // Context-aware suggestions based on user data (matches Next.js exactly)
    if (userContext) {
      const items: string[] = []
      const hunts = userContext.quote_requests || []
      const quotes = userContext.quotes || []
      const contracts = userContext.contracts || []

      if (hunts.length > 0) items.push('Hvad er status på mine jagter?')
      if (quotes.length > 0) items.push(`Sammenlign mine ${quotes.length} tilbud`)
      if (contracts.length > 0) items.push('Betaler jeg for meget lige nu?')
      items.push('Hvad kan du hjælpe mig med?')
      for (const d of DEFAULT_SUGGESTIONS) {
        if (items.length >= 4) break
        if (!items.includes(d)) items.push(d)
      }
      return items.slice(0, 4)
    }
    return DEFAULT_SUGGESTIONS
  }, [inboxMessageId, providerParam, userContext])

  const sendingRef = useRef(false)
  const sendMessage = useCallback(async (text: string) => {
    if (sendingRef.current) return
    sendingRef.current = true
    haptics.light()
    events.chatMessageSent()
    userMessageCountRef.current++
    const userMessage: ChatMessage = { role: 'user', content: text, timestamp: new Date() }
    setMessages(prev => [...prev, userMessage])
    setInput('')
    setIsLoading(true)
    setLastFailedMessage(null)
    // Detect if this will trigger web search (§ references → retsinformation.dk lookup)
    setIsWebSearchQuery(/§\s*\d|paragraf|elforsyningsloven §|forbrugeraftaleloven §/i.test(text))

    try {
      const updatedHistory = [...conversationHistory.slice(-30), { role: 'user', content: text }]

      // Read dashboard savings from shared cache — set by index.tsx when dashboard
      // computes its state. This ensures chat shows the SAME numbers as dashboard.
      // If cache is empty (user opened chat before dashboard), we pass undefined
      // and backend falls back to market_plans.
      const cachedSavings = getCachedDashboardSavings()
      const dashboardState = cachedSavings && cachedSavings.totalSavings > 0 ? {
        total_savings: cachedSavings.totalSavings,
        savings_by_category: cachedSavings.savingsByCategory,
        best_per_category: cachedSavings.bestPerCategory,
      } : undefined

      const res = await apiClient.chat.send(
        text,
        updatedHistory,
        inboxMessageId || undefined,
        undefined,
        dashboardState,
      )
      const data = res.data

      // Use contextual fallback when response is empty
      const fallbackContent = data?.email_draft
        ? `Her er dit udkast til ${data.email_draft.provider_name || 'udbyderen'}.`
        : 'Jeg kunne ikke behandle din forespørgsel.'
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data?.response || fallbackContent,
        timestamp: new Date(),
        actionCards: data?.action_cards || undefined,
        emailSuggestion: data?.email_suggestion || undefined,
        emailDraft: data?.email_draft || undefined,
        draftStatus: data?.email_draft ? 'pending' : undefined,
      }])
      // Feedback trigger after 3 user messages
      if (userMessageCountRef.current === 3) {
        setChatEngaged(true)
      }
    } catch (error: any) {
      haptics.error()
      setLastFailedMessage(text)
      const is503 = error?.response?.status === 503
      sentryCaptureException(error, { action: 'chat_send', is_config_error: is503 })
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: is503
          ? 'AI-assistenten er ikke konfigureret i dette miljø. Tilføj OPENAI_API_KEY for at aktivere chat.'
          : 'Der opstod en fejl — vores team er notificeret. Prøv igen.',
        timestamp: new Date(),
        isError: true,
      }])
    } finally {
      setIsLoading(false)
      setIsWebSearchQuery(false)
      sendingRef.current = false
    }
  }, [conversationHistory, inboxMessageId])

  const retryLastMessage = () => {
    if (!lastFailedMessage) return
    setMessages(prev => prev.filter(m => !m.isError))
    sendMessage(lastFailedMessage)
  }

  const handleSubmit = () => {
    if (!input.trim() || isLoading) return
    sendMessage(input)
  }

  const executeCancelSwitch = async () => {
    if (!cancelConfirm) return
    const category = cancelConfirm.href?.split('/').pop()
    if (!category) return
    try {
      const res = await apiClient.quotes.cancelSwitch(category)
      setCancelConfirm(null)
      const emailSent = res.data?.email_sent
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: emailSent
          ? `Dit skift er annulleret. Vi har sendt en annullering til selskabet. Du kan se andre tilbud på tilbudssiden.`
          : `Dit skift er annulleret. Vi kunne ikke sende annulleringen automatisk — kontakt os via chatten hvis du har spørgsmål.`,
        timestamp: new Date(),
      }])
      toast.success('Skiftet er annulleret')
      appEvents.emitDebounced('inbox-updated')
    } catch (err: any) {
      sentryCaptureException(err, { action: 'cancel_switch', screen: 'chat' })
      toast.error('Kunne ikke annullere skiftet — vi er notificeret. Prøv igen.')
      setCancelConfirm(null)
    }
  }

  const handleAcceptSuggestion = (suggestion: EmailSuggestion) => {
    sendMessage(`Ja, skriv et udkast til ${suggestion.provider_name}`)
  }

  const handleSendDraft = async (messageIndex: number) => {
    const msg = messages[messageIndex]
    if (!msg.emailDraft) return

    setMessages(prev => prev.map((m, i) =>
      i === messageIndex ? { ...m, draftStatus: 'sending' as const } : m
    ))

    try {
      await apiClient.chat.sendDraft(msg.emailDraft)
      setMessages(prev => prev.map((m, i) =>
        i === messageIndex ? { ...m, draftStatus: 'sent' as const } : m
      ))
      const providerLabel = msg.emailDraft.provider_name || msg.emailDraft.to_email || 'selskabet'
      toast.success(`Email sendt til ${providerLabel}`)
      setMessages(prev => [...prev, {
        role: 'assistant' as const,
        content: `Email sendt til ${providerLabel}. Du kan se den i din indbakke.`,
        timestamp: new Date(),
      }])
    } catch (err: any) {
      sentryCaptureException(err, { action: 'send_draft', screen: 'chat' })
      setMessages(prev => prev.map((m, i) =>
        i === messageIndex ? { ...m, draftStatus: 'failed' as const } : m
      ))
      toast.error('Kunne ikke sende email — vi er notificeret. Prøv igen.')
    }
  }

  const handleEditDraft = (messageIndex: number) => {
    setMessages(prev => prev.map((m, i) =>
      i === messageIndex ? { ...m, draftStatus: 'editing' as const } : m
    ))
  }

  const handleUpdateDraft = (messageIndex: number, updated: any) => {
    setMessages(prev => prev.map((m, i) =>
      i === messageIndex ? { ...m, emailDraft: updated, draftStatus: 'pending' as const } : m
    ))
  }

  const handleCancelDraft = (messageIndex: number) => {
    setMessages(prev => prev.map((m, i) =>
      i === messageIndex ? { ...m, emailDraft: undefined, emailSuggestion: undefined, draftStatus: undefined } : m
    ))
  }

  const renderMessage = ({ item, index }: { item: ChatMessage; index: number }) => {
    const isUser = item.role === 'user'
    const hasBreakoutCards = !isUser && (
      (item.actionCards && item.actionCards.length > 0) ||
      (item.emailSuggestion && !item.emailDraft) ||
      item.emailDraft
    )

    // Date separator: show header when date changes between messages
    let dateSeparator: React.ReactNode = null
    if (index > 0) {
      const prevDate = messages[index - 1].timestamp
      const currDate = item.timestamp
      if (prevDate.toDateString() !== currDate.toDateString()) {
        const today = new Date()
        const yesterday = new Date(today)
        yesterday.setDate(yesterday.getDate() - 1)
        let label: string
        if (currDate.toDateString() === today.toDateString()) {
          label = 'I dag'
        } else if (currDate.toDateString() === yesterday.toDateString()) {
          label = 'I går'
        } else {
          label = currDate.toLocaleDateString('da-DK', { weekday: 'long', day: 'numeric', month: 'long' })
        }
        dateSeparator = (
          <View style={styles.dateSeparator}>
            <View style={styles.dateSeparatorLine} />
            <Text style={styles.dateSeparatorText}>{label}</Text>
            <View style={styles.dateSeparatorLine} />
          </View>
        )
      }
    }

    // Error messages render as a distinct error card
    if (item.isError) {
      return (
        <FadeInMessage>
        {dateSeparator}
        <View style={styles.errorCard}>
          <View style={styles.errorCardContent}>
            <AlertCircle size={16} color={colors.error} />
            <Text style={styles.errorCardText}>{item.content}</Text>
          </View>
          {lastFailedMessage && !isLoading && (
            <PressableScale style={styles.errorRetryBtn} onPress={retryLastMessage} haptic="light">
              <RotateCcw size={13} color={colors.primary} />
              <Text style={styles.errorRetryText}>Prøv igen</Text>
            </PressableScale>
          )}
        </View>
        </FadeInMessage>
      )
    }

    return (
      <FadeInMessage>
      {dateSeparator}
      <View style={styles.messageColumn}>
        {/* Text bubble row */}
        <View
          accessibilityRole="text"
          accessibilityLabel={`${isUser ? 'Dig' : 'ForbrugerAgenten'}: ${item.content.replace(/[*#_`]/g, '').slice(0, 200)}`}
          style={[styles.messageRow, isUser && styles.messageRowUser]}
        >
          {!isUser && (
            <Image source={require('../../assets/agent360-icon.png')} style={styles.botAvatarImage} />
          )}
          <View style={[styles.messageBubble, isUser ? styles.userBubble : styles.botBubble]}>
            <FormattedText content={item.content} isUser={isUser} />
            <Text style={[styles.timestamp, isUser && styles.timestampUser]}>
              {item.timestamp.toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' })}
            </Text>
          </View>
          {isUser && (
            <View style={styles.userAvatar}>
              <User size={16} color={colors.primary} />
            </View>
          )}
        </View>

        {/* Breakout cards — rendered OUTSIDE the bubble for more space and visual separation */}
        {hasBreakoutCards && (
          <View style={styles.cardArea}>
            {/* Action cards */}
            {item.actionCards && item.actionCards.length > 0 && item.actionCards.map((card, ci) => {
              const colorMap: Record<string, { bg: string; border: string; text: string; iconColor: string }> = {
                cancel_switch: { bg: '#fef2f2', border: '#fecaca', text: '#991b1b', iconColor: '#dc2626' },
                start_onboarding: { bg: '#faf5ff', border: '#e9d5ff', text: '#581c87', iconColor: '#9333ea' },
                start_new_hunt: { bg: '#faf5ff', border: '#e9d5ff', text: '#581c87', iconColor: '#9333ea' },
                view_quotes: { bg: '#eff6ff', border: '#bfdbfe', text: '#1e3a5f', iconColor: '#2563eb' },
                view_inbox: { bg: '#fff7ed', border: '#fed7aa', text: '#7c2d12', iconColor: '#ea580c' },
                connect_email: { bg: '#ecfdf5', border: '#a7f3d0', text: '#064e3b', iconColor: '#059669' },
              }
              const c = colorMap[card.type] || colorMap.view_inbox
              const isCancelSwitch = card.type === 'cancel_switch'

              return (
                <PressableScale
                  key={ci}
                  style={styles.breakoutActionCard}
                  onPress={() => {
                    if (isCancelSwitch) {
                      setCancelConfirm(card)
                    } else if (card.href?.startsWith('/')) {
                      router.push(card.href as any)
                    }
                  }}
                  haptic="light"
                >
                  <View style={[styles.actionCardIcon, { backgroundColor: c.bg }]}>
                    {(() => {
                      if (card.icon && ACTION_ICONS[card.icon]) {
                        const IconComponent = ACTION_ICONS[card.icon]
                        return <IconComponent size={18} color={c.iconColor} />
                      }
                      if (isCancelSwitch) return <AlertTriangle size={18} color={c.iconColor} />
                      if (card.type === 'start_onboarding') return <Zap size={18} color={c.iconColor} />
                      if (card.type === 'start_new_hunt') return <RefreshCw size={18} color={c.iconColor} />
                      if (card.type === 'view_quotes') return <BookOpen size={18} color={c.iconColor} />
                      if (card.type === 'view_inbox') return <Inbox size={18} color={c.iconColor} />
                      if (card.type === 'connect_email') return <Link2 size={18} color={c.iconColor} />
                      return null
                    })()}
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={[styles.actionCardLabel, { color: c.text }]}>{card.label}</Text>
                    {card.description && <Text style={[styles.actionCardDesc, { color: c.text + '99' }]}>{card.description}</Text>}
                  </View>
                  <ArrowRight size={16} color={colors.text.muted} />
                </PressableScale>
              )
            })}

            {/* Email suggestion card (Step 1: AI proposes email) */}
            {item.emailSuggestion && !item.emailDraft && (
              <View style={styles.breakoutSuggestionCard}>
                <View style={styles.suggestionHeader}>
                  <View style={styles.suggestionIconCircle}>
                    <Mail size={16} color={colors.primary} />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.suggestionTitle}>
                      {(() => {
                        const actionLabels: Record<string, string> = {
                          klage: 'klage',
                          opsigelse: 'opsigelse',
                          'forespørgsel': 'henvendelse',
                          svar: 'svar',
                        }
                        const action = actionLabels[item.emailSuggestion!.action_type] || 'email'
                        return action.charAt(0).toUpperCase() + action.slice(1)
                      })()}{' '}
                      til {item.emailSuggestion.provider_name}
                    </Text>
                    {item.emailSuggestion.reason ? (
                      <Text style={styles.suggestionReason}>{item.emailSuggestion.reason}</Text>
                    ) : null}
                  </View>
                </View>
                <View style={{ flexDirection: 'row', justifyContent: 'flex-end', marginTop: spacing.sm }}>
                  <PressableScale
                    style={[styles.suggestionCTA, isLoading && { opacity: 0.7 }]}
                    onPress={() => handleAcceptSuggestion(item.emailSuggestion!)}
                    disabled={isLoading}
                    haptic="medium"
                  >
                    {isLoading ? (
                      <>
                        <ActivityIndicator size="small" color={colors.text.inverse} />
                        <Text style={styles.suggestionCTAText}>Genererer...</Text>
                      </>
                    ) : (
                      <>
                        <PenLine size={14} color={colors.text.inverse} />
                        <Text style={styles.suggestionCTAText}>Skriv udkast</Text>
                      </>
                    )}
                  </PressableScale>
                </View>
              </View>
            )}

            {/* Email draft card */}
            {item.emailDraft && (() => {
              const draft = item.emailDraft
              const missingEmail = !draft.to_email || draft.to_email === 'Ukendt email'

              if (item.draftStatus === 'editing') {
                return (
                  <DraftEditCard
                    draft={draft}
                    onSave={(updated) => handleUpdateDraft(index, updated)}
                    onCancel={() => handleUpdateDraft(index, draft)}
                  />
                )
              }

              return (
                <View style={styles.breakoutDraftCard}>
                  {/* Blue header bar */}
                  <View style={styles.draftCardHeader}>
                    <Mail size={14} color="#fff" />
                    <Text style={styles.draftCardHeaderText}>Email-udkast</Text>
                    {draft.provider_name && (
                      <Text style={styles.draftCardProvider}>{draft.provider_name}</Text>
                    )}
                    {item.draftStatus === 'sent' && (
                      <View style={[styles.draftStatusBadge, { backgroundColor: 'rgba(255,255,255,0.2)' }]}>
                        <CheckCircle2 size={12} color="#fff" />
                        <Text style={[styles.draftStatusText, { color: '#fff' }]}>Sendt</Text>
                      </View>
                    )}
                    {item.draftStatus === 'failed' && (
                      <View style={[styles.draftStatusBadge, { backgroundColor: 'rgba(255,255,255,0.2)' }]}>
                        <XCircle size={12} color="#fca5a5" />
                        <Text style={[styles.draftStatusText, { color: '#fca5a5' }]}>Fejl</Text>
                      </View>
                    )}
                  </View>

                  {/* Email body */}
                  <View style={styles.draftCardBody}>
                    {missingEmail ? (
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                        <AlertTriangle size={14} color={colors.error} />
                        <Text style={styles.draftTo}>
                          Til: <Text style={styles.draftToMissing}>Email mangler — tryk Rediger</Text>
                        </Text>
                      </View>
                    ) : (
                      <Text style={styles.draftTo}>
                        Til: <Text style={styles.draftToEmail}>{draft.to_email}</Text>
                      </Text>
                    )}
                    <Text style={styles.draftSubject} numberOfLines={1}>
                      Emne: <Text style={{ fontWeight: '600', color: colors.text.primary }}>{draft.subject}</Text>
                    </Text>

                    {/* Collapsible body */}
                    {expandedDrafts.has(index) ? (
                      <View style={styles.draftBodyContainer}>
                        <Text style={styles.draftBody}>{draft.body}</Text>
                        <Pressable
                          style={styles.draftExpandBtn}
                          onPress={() => setExpandedDrafts(prev => { const next = new Set(prev); next.delete(index); return next })}
                        >
                          <Text style={styles.draftExpandText}>Skjul</Text>
                          <ChevronUp size={14} color={colors.primary} />
                        </Pressable>
                      </View>
                    ) : (
                      <View style={styles.draftBodyContainer}>
                        <Text style={styles.draftBody} numberOfLines={3}>{draft.body}</Text>
                        <Pressable
                          style={styles.draftExpandBtn}
                          onPress={() => setExpandedDrafts(prev => { const next = new Set(prev); next.add(index); return next })}
                        >
                          <Text style={styles.draftExpandText}>Vis hele udkastet</Text>
                          <ChevronDown size={14} color={colors.primary} />
                        </Pressable>
                      </View>
                    )}

                    {/* Email blocker + actions */}
                    {(item.draftStatus === 'pending' || item.draftStatus === 'failed') && (
                      <>
                        {userContext && !userContext.email_connected && (
                          <PressableScale
                            style={styles.emailBlocker}
                            onPress={() => router.push('/settings' as any)}
                            haptic="light"
                          >
                            <Link2 size={14} color="#d97706" />
                            <View style={{ flex: 1 }}>
                              <Text style={styles.emailBlockerTitle}>Forbind din email først</Text>
                              <Text style={styles.emailBlockerDesc}>For at sende emails direkte</Text>
                            </View>
                            <ArrowRight size={14} color="#d97706" />
                          </PressableScale>
                        )}
                        <View style={styles.draftCardToolbar}>
                          <PressableScale
                            style={[styles.draftSendBtn, (missingEmail || (userContext && !userContext.email_connected)) && { opacity: 0.5 }]}
                            onPress={() => handleSendDraft(index)}
                            disabled={missingEmail || (userContext && !userContext.email_connected)}
                            haptic="medium"
                          >
                            <Send size={12} color={colors.text.inverse} />
                            <Text style={styles.draftSendText}>Send</Text>
                          </PressableScale>
                          <Pressable style={styles.draftEditBtn} onPress={() => handleEditDraft(index)}>
                            <Edit3 size={12} color={colors.primary} />
                            <Text style={styles.draftEditText}>Rediger</Text>
                          </Pressable>
                          <Pressable style={styles.draftCancelBtn} onPress={() => handleCancelDraft(index)}>
                            <X size={14} color={colors.text.muted} />
                          </Pressable>
                        </View>
                      </>
                    )}

                    {item.draftStatus === 'sending' && (
                      <View style={styles.draftCardToolbar}>
                        <ActivityIndicator size="small" color={colors.primary} />
                        <Text style={{ ...typography.small, color: colors.text.muted, marginLeft: 8 }}>Sender...</Text>
                      </View>
                    )}
                  </View>
                </View>
              )
            })()}
          </View>
        )}
      </View>
      </FadeInMessage>
    )
  }

  const showSuggestions = (messages.length <= 2 || showIdleSuggestions) && !input.trim()

  return (
    <SafeAreaView style={styles.container} edges={[]}>
      <ContentContainer>
      {/* Context banner */}
      {inboxMessageId && providerParam && (
        <View style={styles.contextBanner}>
          <Mail size={16} color={colors.primary} />
          <Text style={styles.contextText}>
            Samtale om besked fra <Text style={{ fontWeight: '600' }}>{providerParam}</Text>
          </Text>
        </View>
      )}

      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        keyboardVerticalOffset={Platform.OS === 'ios' ? 90 : 0}
      >
        <FlatList
          ref={flatListRef}
          data={messages}
          renderItem={renderMessage}
          keyExtractor={(_, i) => String(i)}
          style={{ flex: 1 }}
          contentContainerStyle={styles.messageList}
          keyboardShouldPersistTaps="handled"
          onContentSizeChange={() => flatListRef.current?.scrollToEnd({ animated: true })}
          ListFooterComponent={
            <>
              {/* Loading indicator with typing text */}
              {isLoading && (
                <View style={[styles.messageRow]}>
                  <Image source={require('../../assets/agent360-icon.png')} style={styles.botAvatarImage} />
                  <View style={[styles.messageBubble, styles.botBubble]}>
                    {isWebSearchQuery ? (
                      // Web search indicator — shown when user asks about specific § paragraphs
                      <View style={{ gap: 6 }}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                          <TypingIndicator />
                          <Text style={{ ...typography.small, color: colors.text.muted }}>
                            Søger lovbasen...
                          </Text>
                        </View>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                          <Globe size={11} color={colors.primary} />
                          <Text style={{ ...typography.small, color: colors.primary, fontSize: 11 }}>
                            retsinformation.dk
                          </Text>
                        </View>
                      </View>
                    ) : (
                      <View style={{ gap: 4 }}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                          <TypingIndicator />
                          <Text style={{ ...typography.small, color: colors.text.muted }}>
                            {slowLoading ? 'Det tager lidt længere...' : 'Skriver...'}
                          </Text>
                        </View>
                        {slowLoading && (
                          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                            <Clock size={12} color={colors.text.muted} />
                            <Text style={{ ...typography.small, color: colors.text.muted, fontSize: 11 }}>
                              AI-modellen tænker grundigt — svar kommer snart
                            </Text>
                          </View>
                        )}
                      </View>
                    )}
                  </View>
                </View>
              )}
            </>
          }
        />

        {/* Cancel switch confirmation — modal overlay */}
        <Modal
          visible={!!cancelConfirm}
          transparent
          animationType="fade"
          onRequestClose={() => setCancelConfirm(null)}
        >
          <Pressable style={styles.modalBackdrop} onPress={() => setCancelConfirm(null)}>
            <Pressable style={styles.cancelConfirmModal} onPress={(e) => e.stopPropagation()}>
              <View style={styles.cancelConfirmIconRow}>
                <View style={styles.cancelConfirmIcon}>
                  <AlertTriangle size={24} color="#dc2626" />
                </View>
              </View>
              <Text style={styles.cancelConfirmTitle}>{cancelConfirm?.label}?</Text>
              {cancelConfirm?.description && (
                <Text style={styles.cancelConfirmDesc}>{cancelConfirm.description}</Text>
              )}
              <View style={styles.cancelConfirmButtons}>
                <PressableScale
                  style={styles.cancelConfirmYes}
                  onPress={executeCancelSwitch}
                  haptic="medium"
                >
                  <Text style={styles.cancelConfirmYesText}>Ja, annuller skiftet</Text>
                </PressableScale>
                <Pressable style={styles.cancelConfirmNo} onPress={() => setCancelConfirm(null)}>
                  <Text style={styles.cancelConfirmNoText}>Nej, behold</Text>
                </Pressable>
              </View>
            </Pressable>
          </Pressable>
        </Modal>

        {/* Bottom area: suggestions pinned directly above input */}
        {showSuggestions && (
          <View style={styles.suggestionsGrid}>
            {suggestions.map((q, i) => (
              <PressableScale key={i} accessibilityRole="button" accessibilityLabel={`Foreslået spørgsmål: ${q}`} style={styles.suggestionButton} onPress={() => { events.chatSuggestionUsed(q); sendMessage(q) }} disabled={isLoading} haptic="light">
                <Text style={styles.suggestionText}>{q}</Text>
              </PressableScale>
            ))}
          </View>
        )}

        {/* Input */}
        <View style={styles.inputContainer}>
          <TextInput
            style={styles.input}
            placeholder="Stil et spørgsmål · AI kan lave fejl"
            placeholderTextColor={colors.text.muted}
            value={input}
            onChangeText={setInput}
            multiline
            maxLength={2000}
            editable={!isLoading}
            onSubmitEditing={handleSubmit}
            returnKeyType="send"
            {...(Platform.OS === 'web' ? {
              onKeyPress: (e: any) => {
                if (e.nativeEvent.key === 'Enter' && !e.nativeEvent.shiftKey) {
                  e.preventDefault()
                  handleSubmit()
                }
              },
            } : {})}
          />
          <PressableScale
            accessibilityRole="button"
            accessibilityLabel="Send besked"
            style={[styles.sendButton, (!input.trim() || isLoading) && styles.sendButtonDisabled]}
            onPress={handleSubmit}
            disabled={!input.trim() || isLoading}
            haptic="medium"
          >
            <Send size={20} color={input.trim() && !isLoading ? colors.text.inverse : colors.text.muted} />
          </PressableScale>
        </View>
      </KeyboardAvoidingView>
      </ContentContainer>
    </SafeAreaView>
  )
}

function formatInline(text: string): React.ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/g)
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <Text key={i} style={{ fontWeight: '600' }}>{part.slice(2, -2)}</Text>
    }
    return part
  })
}

function FormattedText({ content, isUser }: { content: string; isUser: boolean }) {
  if (isUser) return <Text style={[styles.messageText, { color: colors.text.inverse }]}>{content}</Text>

  const paragraphs = content.split(/\n\n+/)
  return (
    <View style={{ gap: 6 }}>
      {paragraphs.map((para, i) => {
        const lines = para.split('\n')
        const isBulletList = lines.every(l => /^[-•*]\s/.test(l.trim()) || l.trim() === '')
        const isNumberedList = lines.every(l => /^\d+[.)]\s/.test(l.trim()) || l.trim() === '')

        if (isBulletList) {
          return (
            <View key={i} style={{ gap: 2 }}>
              {lines.filter(l => l.trim()).map((line, j) => (
                <View key={j} style={{ flexDirection: 'row', gap: 6 }}>
                  <Text style={{ color: colors.primary, fontSize: 12, marginTop: 1 }}>•</Text>
                  <Text style={[styles.messageText, { flex: 1 }]}>{formatInline(line.replace(/^[-•*]\s*/, ''))}</Text>
                </View>
              ))}
            </View>
          )
        }

        if (isNumberedList) {
          return (
            <View key={i} style={{ gap: 4 }}>
              {lines.filter(l => l.trim()).map((line, j) => (
                <View key={j} style={{ flexDirection: 'row', gap: 6 }}>
                  <Text style={{ color: colors.primary, fontSize: 12, fontWeight: '500', minWidth: 16, marginTop: 1 }}>{j + 1}.</Text>
                  <Text style={[styles.messageText, { flex: 1 }]}>{formatInline(line.replace(/^\d+[.)]\s*/, ''))}</Text>
                </View>
              ))}
            </View>
          )
        }

        return <Text key={i} style={styles.messageText}>{formatInline(para.replace(/\n/g, ' '))}</Text>
      })}
    </View>
  )
}

function DraftEditCard({ draft, onSave, onCancel }: { draft: any; onSave: (updated: any) => void; onCancel: () => void }) {
  const [editSubject, setEditSubject] = useState(draft.subject || '')
  const [editBody, setEditBody] = useState(draft.body || '')

  return (
    <View style={styles.draftEditCard}>
      <View style={styles.draftEditHeader}>
        <Edit3 size={14} color={colors.primary} />
        <Text style={styles.draftEditHeaderText}>Rediger udkast</Text>
      </View>
      <View style={styles.draftEditContent}>
        <Text style={styles.draftEditLabel}>Emne</Text>
        <TextInput
          style={styles.draftEditInput}
          value={editSubject}
          onChangeText={setEditSubject}
          placeholder="Emne..."
          placeholderTextColor={colors.text.muted}
        />
        <Text style={[styles.draftEditLabel, { marginTop: 8 }]}>Indhold</Text>
        <TextInput
          style={[styles.draftEditInput, { minHeight: 120, textAlignVertical: 'top' }]}
          value={editBody}
          onChangeText={setEditBody}
          multiline
          placeholder="Skriv indhold..."
          placeholderTextColor={colors.text.muted}
        />
      </View>
      <View style={styles.draftEditActions}>
        <Pressable
          style={styles.draftSaveBtn}
          onPress={() => onSave({ ...draft, subject: editSubject, body: editBody })}
        >
          <Save size={12} color={colors.text.inverse} />
          <Text style={styles.draftSendText}>Gem ændringer</Text>
        </Pressable>
        <Pressable style={styles.draftEditCancelBtn} onPress={onCancel}>
          <Text style={styles.draftEditCancelText}>Annuller</Text>
        </Pressable>
      </View>
    </View>
  )
}

function FadeInMessage({ children }: { children: React.ReactNode }) {
  const anim = useRef(new Animated.Value(0)).current
  useEffect(() => {
    Animated.timing(anim, { toValue: 1, duration: 250, useNativeDriver: true }).start()
  }, [])
  return (
    <Animated.View style={{ opacity: anim, transform: [{ translateY: anim.interpolate({ inputRange: [0, 1], outputRange: [8, 0] }) }] }}>
      {children}
    </Animated.View>
  )
}

function TypingIndicator() {
  const dot1 = useRef(new Animated.Value(0.3)).current
  const dot2 = useRef(new Animated.Value(0.3)).current
  const dot3 = useRef(new Animated.Value(0.3)).current

  useEffect(() => {
    const animate = (dot: Animated.Value, delay: number) =>
      Animated.loop(
        Animated.sequence([
          Animated.delay(delay),
          Animated.timing(dot, { toValue: 1, duration: 400, useNativeDriver: true }),
          Animated.timing(dot, { toValue: 0.3, duration: 400, useNativeDriver: true }),
        ])
      )
    const a1 = animate(dot1, 0)
    const a2 = animate(dot2, 200)
    const a3 = animate(dot3, 400)
    a1.start(); a2.start(); a3.start()
    return () => { a1.stop(); a2.stop(); a3.stop() }
  }, [])

  return (
    <View style={styles.typingDots}>
      {[dot1, dot2, dot3].map((dot, i) => (
        <Animated.View key={i} style={[styles.dot, { opacity: dot }]} />
      ))}
    </View>
  )
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.background },
  contextBanner: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.xs,
    marginHorizontal: spacing.md, marginTop: spacing.xs,
    paddingHorizontal: spacing.sm, paddingVertical: spacing.xs,
    backgroundColor: colors.primaryLight, borderRadius: borderRadius.sm,
    borderWidth: 1, borderColor: '#bfdbfe',
  },
  contextText: { ...typography.small, color: colors.primaryDark },
  messageList: { padding: spacing.md, paddingBottom: spacing.sm },
  // Column wrapper for message bubble + breakout cards
  messageColumn: { marginBottom: spacing.md },
  messageRow: { flexDirection: 'row', gap: spacing.xs, alignItems: 'flex-end' },
  messageRowUser: { justifyContent: 'flex-end' },
  botAvatarImage: { width: 32, height: 32, borderRadius: 10 },
  userAvatar: {
    width: 32, height: 32, borderRadius: 10, backgroundColor: colors.primaryLight,
    alignItems: 'center', justifyContent: 'center',
  },
  messageBubble: { maxWidth: '80%', borderRadius: 18, paddingHorizontal: 16, paddingVertical: 12 },
  userBubble: { backgroundColor: colors.primary },
  botBubble: { backgroundColor: colors.background, ...shadows.sm, borderWidth: 1, borderColor: colors.borderLight },
  messageText: { ...typography.caption, color: colors.text.primary, lineHeight: 20 },
  timestamp: { ...typography.small, color: colors.text.muted, marginTop: 4 },
  timestampUser: { color: 'rgba(255,255,255,0.6)' },
  typingDots: { flexDirection: 'row', gap: 4, paddingVertical: 4 },
  dot: { width: 6, height: 6, borderRadius: 3, backgroundColor: colors.primary },
  // Error card (replaces plain error bubble + standalone retry)
  errorCard: {
    marginHorizontal: spacing.sm, marginBottom: spacing.sm, marginLeft: 36 + spacing.sm,
    maxWidth: 600,
    backgroundColor: '#fef2f2', borderRadius: borderRadius.md,
    borderWidth: 1, borderColor: '#fecaca',
    borderLeftWidth: 3, borderLeftColor: '#dc2626',
    padding: spacing.sm, gap: spacing.xs,
  },
  errorCardContent: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.sm,
  },
  errorCardText: { ...typography.caption, color: '#991b1b', flex: 1 },
  errorRetryBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 4,
    alignSelf: 'flex-end', paddingVertical: 4, paddingHorizontal: spacing.sm,
    borderRadius: borderRadius.sm, backgroundColor: colors.background,
    borderWidth: 1, borderColor: colors.border,
  },
  errorRetryText: { ...typography.small, color: colors.primary, fontWeight: '600' },

  // ── Breakout card area (below message bubble) ──
  cardArea: {
    marginLeft: 36, // avatar (32px) + gap (4px)
    marginTop: spacing.sm,
    gap: spacing.sm,
    maxWidth: 600,
  },

  // Breakout action cards
  breakoutActionCard: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.sm,
    padding: spacing.sm,
    backgroundColor: colors.background,
    borderRadius: borderRadius.md,
    borderWidth: 1, borderColor: colors.border,
    ...shadows.sm,
  },
  actionCardIcon: {
    width: 36, height: 36, borderRadius: borderRadius.sm,
    alignItems: 'center', justifyContent: 'center',
  },
  actionCardLabel: { ...typography.captionBold, color: colors.text.primary },
  actionCardDesc: { ...typography.small, color: colors.text.muted, marginTop: 1 },

  // Breakout email suggestion card (left blue accent)
  breakoutSuggestionCard: {
    backgroundColor: colors.background,
    borderRadius: borderRadius.md,
    borderWidth: 1, borderColor: colors.border,
    borderLeftWidth: 3, borderLeftColor: colors.primary,
    padding: spacing.md,
    ...shadows.sm,
  },
  suggestionHeader: { flexDirection: 'row', alignItems: 'flex-start', gap: spacing.sm },
  suggestionIconCircle: {
    width: 36, height: 36, borderRadius: 18, backgroundColor: colors.primaryLight,
    alignItems: 'center', justifyContent: 'center',
  },
  suggestionTitle: { ...typography.captionBold, color: colors.text.primary },
  suggestionReason: { ...typography.small, color: colors.text.muted, marginTop: 2, lineHeight: 16 },
  suggestionCTA: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6,
    paddingVertical: 8, paddingHorizontal: spacing.md, backgroundColor: colors.primary,
    borderRadius: borderRadius.md,
  },
  suggestionCTAText: { ...typography.captionBold, color: colors.text.inverse },

  // Breakout email draft card (blue header bar)
  breakoutDraftCard: {
    backgroundColor: colors.background,
    borderRadius: borderRadius.md,
    overflow: 'hidden',
    ...shadows.md,
    borderWidth: 1, borderColor: colors.border,
  },
  draftCardHeader: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.xs,
    paddingHorizontal: spacing.sm, paddingVertical: 10,
    backgroundColor: colors.primary,
  },
  draftCardHeaderText: { ...typography.captionBold, color: '#fff' },
  draftCardProvider: { ...typography.small, color: 'rgba(255,255,255,0.7)', marginLeft: 'auto' },
  draftCardBody: { padding: spacing.sm, gap: spacing.xs },
  draftTo: { ...typography.small, color: colors.text.secondary },
  draftSubject: { ...typography.caption, color: colors.text.primary },
  draftBodyContainer: {
    marginTop: spacing.xs, backgroundColor: colors.surface,
    borderRadius: borderRadius.sm, borderWidth: 1, borderColor: colors.borderLight,
    padding: spacing.sm,
  },
  draftBody: { ...typography.small, color: colors.text.secondary, lineHeight: 18 },
  draftExpandBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 4,
    marginTop: 6, paddingVertical: 4,
  },
  draftExpandText: { ...typography.small, color: colors.primary, fontWeight: '500' },
  draftCardToolbar: {
    flexDirection: 'row', gap: spacing.xs, marginTop: spacing.xs, paddingTop: spacing.xs,
    borderTopWidth: 1, borderTopColor: colors.borderLight, alignItems: 'center',
  },
  draftToMissing: { color: colors.error, fontWeight: '500' },
  draftToEmail: { color: colors.text.primary },
  draftSendBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 4, flex: 1,
    paddingVertical: 8, paddingHorizontal: spacing.sm,
    backgroundColor: colors.primary, borderRadius: borderRadius.sm,
    justifyContent: 'center',
  },
  draftSendText: { ...typography.small, color: colors.text.inverse, fontWeight: '500' },
  draftEditBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 4,
    paddingVertical: 8, paddingHorizontal: spacing.sm,
    borderRadius: borderRadius.sm, borderWidth: 1, borderColor: colors.border,
    justifyContent: 'center',
  },
  draftEditText: { ...typography.small, color: colors.text.secondary, fontWeight: '500' },
  draftCancelBtn: { paddingVertical: 8, paddingHorizontal: 10 },
  draftStatusBadge: {
    flexDirection: 'row', alignItems: 'center', gap: 4,
    paddingHorizontal: 8, paddingVertical: 3, borderRadius: 6,
  },
  draftStatusText: { ...typography.small, fontWeight: '500' },

  // Email connection blocker
  emailBlocker: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.sm,
    marginTop: spacing.xs,
    paddingHorizontal: spacing.sm, paddingVertical: spacing.sm,
    backgroundColor: '#fffbeb', borderRadius: borderRadius.sm,
    borderWidth: 1, borderColor: '#fcd34d',
  },
  emailBlockerTitle: { ...typography.captionBold, color: '#92400e' },
  emailBlockerDesc: { ...typography.small, color: '#b45309' },

  // Draft edit card
  draftEditCard: {
    borderWidth: 1, borderColor: '#bfdbfe',
    borderRadius: borderRadius.md, backgroundColor: '#eff6ff', overflow: 'hidden',
  },
  draftEditHeader: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.xs,
    paddingHorizontal: spacing.sm, paddingVertical: spacing.xs,
    backgroundColor: '#dbeafe', borderBottomWidth: 1, borderBottomColor: '#bfdbfe',
  },
  draftEditHeaderText: { ...typography.small, fontWeight: '500', color: colors.primaryDark },
  draftEditContent: { padding: spacing.sm },
  draftEditLabel: { ...typography.small, color: colors.text.muted, marginBottom: 4 },
  draftEditInput: {
    ...typography.small, color: colors.text.primary,
    borderWidth: 1, borderColor: colors.border, borderRadius: borderRadius.sm,
    padding: spacing.xs, backgroundColor: colors.background,
  },
  draftEditActions: {
    flexDirection: 'row', gap: spacing.xs, alignItems: 'center',
    paddingHorizontal: spacing.sm, paddingVertical: spacing.xs,
    borderTopWidth: 1, borderTopColor: '#bfdbfe',
  },
  draftEditCancelBtn: { paddingVertical: 8, paddingHorizontal: spacing.sm },
  draftEditCancelText: { ...typography.small, color: colors.text.muted },
  draftSaveBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 4, flex: 1,
    paddingVertical: 8, paddingHorizontal: spacing.sm,
    backgroundColor: colors.primary, borderRadius: borderRadius.sm,
    justifyContent: 'center',
  },

  // Suggestion chips — multi-row wrap (matches Inbox filter-tabs pattern)
  suggestionsGrid: {
    flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs,
    paddingHorizontal: spacing.md, paddingVertical: spacing.sm,
    borderTopWidth: 1, borderTopColor: colors.borderLight,
    backgroundColor: colors.background,
    maxWidth: 640, alignSelf: 'center', width: '100%',
  },
  suggestionButton: {
    paddingHorizontal: spacing.sm + 4, paddingVertical: spacing.sm,
    backgroundColor: colors.background, borderWidth: 1, borderColor: colors.border,
    borderRadius: borderRadius.lg,
    flexShrink: 1,
  },
  suggestionText: { ...typography.caption, color: colors.text.primary, lineHeight: 18, fontWeight: '500' },

  // Input area
  inputContainer: {
    flexDirection: 'row', paddingHorizontal: spacing.md, paddingVertical: spacing.sm,
    alignItems: 'center', gap: spacing.sm,
    backgroundColor: colors.background,
  },
  input: {
    flex: 1, backgroundColor: colors.surface, borderRadius: borderRadius.lg,
    paddingHorizontal: spacing.md, paddingVertical: spacing.sm,
    ...typography.body, color: colors.text.primary, maxHeight: 100, minHeight: 44,
    borderWidth: 1, borderColor: colors.border,
  },
  sendButton: {
    width: 44, height: 44, borderRadius: 22, backgroundColor: colors.primary,
    alignItems: 'center', justifyContent: 'center',
    ...shadows.colored(colors.primary),
  },
  sendButtonDisabled: { backgroundColor: colors.borderLight },

  // Date separator
  dateSeparator: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.sm,
    marginVertical: spacing.md, paddingHorizontal: spacing.sm,
  },
  dateSeparatorLine: { flex: 1, height: 1, backgroundColor: colors.borderLight },
  dateSeparatorText: { ...typography.small, color: colors.text.muted, fontWeight: '500' },

  // Cancel confirm modal
  modalBackdrop: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center', alignItems: 'center',
    padding: spacing.lg,
  },
  cancelConfirmModal: {
    backgroundColor: colors.background, borderRadius: borderRadius.lg,
    padding: spacing.lg, width: '100%', maxWidth: 340,
    ...shadows.lg,
  },
  cancelConfirmIconRow: { alignItems: 'center', marginBottom: spacing.md },
  cancelConfirmIcon: {
    width: 48, height: 48, borderRadius: 24, backgroundColor: '#fef2f2',
    alignItems: 'center', justifyContent: 'center',
  },
  cancelConfirmTitle: { ...typography.h3, color: '#991b1b', textAlign: 'center', marginBottom: 4 },
  cancelConfirmDesc: { ...typography.caption, color: '#b91c1c', textAlign: 'center', marginBottom: spacing.md },
  cancelConfirmButtons: { gap: spacing.sm },
  cancelConfirmYes: {
    paddingVertical: 12, backgroundColor: '#dc2626',
    borderRadius: borderRadius.md, alignItems: 'center',
  },
  cancelConfirmYesText: { ...typography.captionBold, color: '#fff' },
  cancelConfirmNo: {
    paddingVertical: 12,
    borderWidth: 1, borderColor: colors.border, borderRadius: borderRadius.md, alignItems: 'center',
  },
  cancelConfirmNoText: { ...typography.caption, color: colors.text.secondary },
})
